import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorIcon,
  LockReset as LockResetIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import {
  applyActionCode,
  verifyPasswordResetCode,
  confirmPasswordReset
} from 'firebase/auth';
import { auth } from '../../services/firebase';

export default function AuthAction() {
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const mode = searchParams.get('mode'); // 'verifyEmail' | 'resetPassword' | 'recoverEmail'
  const oobCode = searchParams.get('oobCode');
  const continueUrl = searchParams.get('continueUrl') || '/profile';

  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [accountEmail, setAccountEmail] = useState('');

  // Password reset fields
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetting, setResetting] = useState(false);

  // Prevent double-execution in React 18
  const actionExecuted = useRef(false);

  const resolveTargetUrl = (urlStr) => {
    if (!urlStr) return '/profile';
    try {
      if (urlStr.startsWith('http://') || urlStr.startsWith('https://')) {
        const u = new URL(urlStr);
        if (typeof window !== 'undefined' && (u.host === window.location.host || u.host === 'verveai.co')) {
          return u.pathname + u.search + u.hash;
        }
        return urlStr;
      }
      return urlStr.startsWith('/') ? urlStr : `/${urlStr}`;
    } catch (e) {
      return '/profile';
    }
  };

  const handleContinue = (urlStr) => {
    const target = resolveTargetUrl(urlStr);
    if (target.startsWith('http://') || target.startsWith('https://')) {
      window.location.href = target;
    } else {
      navigate(target);
    }
  };

  useEffect(() => {
    if (!oobCode || !mode) {
      setError('Invalid or missing authentication action code.');
      setLoading(false);
      return;
    }

    if (actionExecuted.current) {
      return;
    }
    actionExecuted.current = true;

    async function handleAction() {
      try {
        setLoading(true);
        setError('');

        if (mode === 'verifyEmail') {
          // Immediately apply verification code
          await applyActionCode(auth, oobCode);
          if (auth.currentUser) {
            try {
              await auth.currentUser.reload();
            } catch (reloadErr) {
              console.warn('Could not reload currentUser immediately:', reloadErr);
            }
          }
          setSuccess(true);
          setLoading(false);

          // Auto-redirect to continueUrl or /profile after 2.5 seconds
          setTimeout(() => {
            handleContinue(continueUrl);
          }, 2500);
        } else if (mode === 'resetPassword') {
          // Verify code and get target email
          const email = await verifyPasswordResetCode(auth, oobCode);
          setAccountEmail(email);
          setLoading(false);
        } else if (mode === 'recoverEmail') {
          await applyActionCode(auth, oobCode);
          setSuccess(true);
          setLoading(false);
        } else {
          setError(`Unsupported authentication mode: '${mode}'`);
          setLoading(false);
        }
      } catch (err) {
        console.error('Auth action error:', err);
        let msg = 'The verification link is invalid, expired, or has already been used.';
        if (err.code === 'auth/expired-action-code') {
          msg = 'This link has expired. Please sign in to request a fresh verification link.';
        } else if (err.code === 'auth/invalid-action-code') {
          msg = 'This link is invalid or has already been used.';
        } else if (err.code === 'auth/user-disabled') {
          msg = 'This account has been disabled. Please contact admin@verveai.co.';
        }
        setError(msg);
        setLoading(false);
      }
    }

    handleAction();
  }, [mode, oobCode, continueUrl]);

  const handlePasswordResetSubmit = async (e) => {
    e.preventDefault();
    if (!newPassword || newPassword.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    try {
      setResetting(true);
      setError('');
      await confirmPasswordReset(auth, oobCode, newPassword);
      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 2500);
    } catch (err) {
      console.error('Password reset failure:', err);
      setError('Failed to reset password: ' + (err.message || 'Please try again.'));
    } finally {
      setResetting(false);
    }
  };

  return (
    <Container component="main" maxWidth="sm" sx={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', py: 6 }}>
      <Paper
        elevation={0}
        sx={{
          p: 5,
          width: '100%',
          textAlign: 'center',
          bgcolor: '#0D111A',
          border: '1px solid #1E293B',
          borderRadius: 3,
          boxShadow: '0 10px 25px rgba(0,0,0,0.5)'
        }}
      >
        {/* Loading State */}
        {loading && (
          <Box sx={{ py: 4 }}>
            <CircularProgress sx={{ color: '#00F5FF', mb: 3 }} size={48} />
            <Typography variant="h5" sx={{ color: '#FFFFFF', fontWeight: 700, mb: 1 }}>
              {mode === 'resetPassword' ? 'Verifying Reset Link...' : 'Verifying Your Email...'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#94A3B8' }}>
              Please wait while we validate your credentials.
            </Typography>
          </Box>
        )}

        {/* Error State */}
        {!loading && error && (
          <Box sx={{ py: 2 }}>
            <ErrorIcon sx={{ fontSize: 64, color: '#EF4444', mb: 2 }} />
            <Typography variant="h5" sx={{ color: '#FFFFFF', fontWeight: 700, mb: 2 }}>
              Verification Failed
            </Typography>
            <Alert severity="error" sx={{ mb: 3, textAlign: 'left', bgcolor: 'rgba(239, 68, 68, 0.1)', color: '#FCA5A5', border: '1px solid rgba(239, 68, 68, 0.2)' }}>
              {error}
            </Alert>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="contained"
                onClick={() => navigate('/login')}
                sx={{
                  bgcolor: '#00F5FF',
                  color: '#07090E',
                  fontWeight: 700,
                  '&:hover': { bgcolor: '#00D1DB' }
                }}
              >
                Go to Sign In
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/')}
                sx={{ borderColor: '#334155', color: '#CBD5E1' }}
              >
                Back to Home
              </Button>
            </Box>
          </Box>
        )}

        {/* Email Verification Success */}
        {!loading && !error && success && mode === 'verifyEmail' && (
          <Box sx={{ py: 2 }}>
            <CheckCircleIcon sx={{ fontSize: 72, color: '#10B981', mb: 2 }} />
            <Typography variant="h4" sx={{ color: '#FFFFFF', fontWeight: 800, mb: 1.5, letterSpacing: '-0.5px' }}>
              Email Verified!
            </Typography>
            <Typography variant="body1" sx={{ color: '#CBD5E1', mb: 3 }}>
              Your email address has been successfully verified. Your account on <strong style={{ color: '#00F5FF' }}>VerveAI Launchpad</strong> is now fully active.
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748B', mb: 4 }}>
              Redirecting you to your profile in a moment...
            </Typography>
            <Button
              variant="contained"
              onClick={() => handleContinue(continueUrl)}
              fullWidth
              sx={{
                py: 1.5,
                background: 'linear-gradient(135deg, #00C6FF 0%, #0072FF 100%)',
                color: '#FFFFFF',
                fontWeight: 700,
                fontSize: '15px'
              }}
            >
              Continue to My Profile
            </Button>
          </Box>
        )}

        {/* Password Reset Form */}
        {!loading && !error && !success && mode === 'resetPassword' && (
          <Box component="form" onSubmit={handlePasswordResetSubmit} sx={{ py: 1 }}>
            <LockResetIcon sx={{ fontSize: 56, color: '#00F5FF', mb: 2 }} />
            <Typography variant="h5" sx={{ color: '#FFFFFF', fontWeight: 700, mb: 1 }}>
              Choose a New Password
            </Typography>
            <Typography variant="body2" sx={{ color: '#94A3B8', mb: 3 }}>
              Resetting password for: <strong style={{ color: '#00F5FF' }}>{accountEmail}</strong>
            </Typography>

            <TextField
              margin="normal"
              required
              fullWidth
              name="newPassword"
              label="New Password (min 6 characters)"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  color: '#FFFFFF',
                  bgcolor: 'rgba(255, 255, 255, 0.03)',
                  '& fieldset': { borderColor: '#334155' },
                  '&:hover fieldset': { borderColor: '#475569' },
                  '&.Mui-focused fieldset': { borderColor: '#00F5FF' }
                },
                '& .MuiInputLabel-root': { color: '#94A3B8' },
                '& .MuiInputLabel-root.Mui-focused': { color: '#00F5FF' }
              }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label="Confirm New Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  color: '#FFFFFF',
                  bgcolor: 'rgba(255, 255, 255, 0.03)',
                  '& fieldset': { borderColor: '#334155' },
                  '&:hover fieldset': { borderColor: '#475569' },
                  '&.Mui-focused fieldset': { borderColor: '#00F5FF' }
                },
                '& .MuiInputLabel-root': { color: '#94A3B8' },
                '& .MuiInputLabel-root.Mui-focused': { color: '#00F5FF' }
              }}
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={resetting}
              sx={{
                py: 1.5,
                background: 'linear-gradient(135deg, #00C6FF 0%, #0072FF 100%)',
                color: '#FFFFFF',
                fontWeight: 700,
                fontSize: '15px'
              }}
            >
              {resetting ? <CircularProgress size={24} sx={{ color: '#FFFFFF' }} /> : 'Update Password'}
            </Button>
          </Box>
        )}

        {/* Password Reset Success */}
        {!loading && !error && success && mode === 'resetPassword' && (
          <Box sx={{ py: 2 }}>
            <CheckCircleIcon sx={{ fontSize: 72, color: '#10B981', mb: 2 }} />
            <Typography variant="h4" sx={{ color: '#FFFFFF', fontWeight: 800, mb: 1.5 }}>
              Password Reset Complete!
            </Typography>
            <Typography variant="body1" sx={{ color: '#CBD5E1', mb: 3 }}>
              Your password has been successfully updated. Redirecting you to sign in...
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/login')}
              fullWidth
              sx={{
                py: 1.5,
                bgcolor: '#00F5FF',
                color: '#07090E',
                fontWeight: 700
              }}
            >
              Sign In Now
            </Button>
          </Box>
        )}
      </Paper>
    </Container>
  );
}
